import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../constants/colors'

export interface DropdownMenuItem {
  id: string
  label: string
  icon?: React.ReactNode
  onClick: () => void
  variant?: 'default' | 'danger'
  disabled?: boolean
}

export interface DropdownMenuProps {
  trigger: React.ReactNode
  items: DropdownMenuItem[]
  className?: string
  placement?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
  width?: number | string
  onOpen?: () => void
  onClose?: () => void
}

/**
 * DropdownMenu Component
 *
 * A design system dropdown menu component with proper theming and accessibility.
 * Supports different placement options and item variants.
 *
 * @param props - Props for the DropdownMenu component
 */
const DropdownMenu: React.FC<DropdownMenuProps> = ({
  trigger,
  items,
  className = '',
  placement = 'bottom-left',
  width = 'auto',
  onOpen,
  onClose
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    onOpen?.()
  }, [onOpen])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    onClose?.()
  }, [onClose])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClose])

  const handleToggle = () => {
    if (isOpen) {
      handleClose()
    } else {
      handleOpen()
    }
  }

  const handleItemClick = (item: DropdownMenuItem) => {
    if (!item.disabled) {
      item.onClick()
      handleClose()
    }
  }

  const getPlacementStyles = () => {
    switch (placement) {
      case 'bottom-right':
        return {
          top: '100%',
          right: 0,
          left: 'auto'
        }
      case 'top-left':
        return {
          bottom: '100%',
          left: 0,
          top: 'auto',
          marginBottom: '8px',
          marginTop: 0
        }
      case 'top-right':
        return {
          bottom: '100%',
          right: 0,
          top: 'auto',
          left: 'auto',
          marginBottom: '8px',
          marginTop: 0
        }
      case 'bottom-left':
      default:
        return {
          top: '100%',
          left: 0,
          right: 'auto'
        }
    }
  }

  return (
    <Box className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger */}
      <Box onClick={handleToggle}>{trigger}</Box>

      {/* Dropdown Menu */}
      {isOpen && (
        <Box
          sx={{
            position: 'absolute',
            width: width,
            backgroundColor: INTERAX_COLORS.offBlack,
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
            zIndex: 50,
            ...getPlacementStyles()
          }}
        >
          <Box sx={{ py: 1 }}>
            {items.map((item, index) => (
              <Box
                key={item.id}
                data-testid={`dropdown-item-${item.id}`}
                onClick={() => handleItemClick(item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  px: 2,
                  py: 1,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s ease',
                  opacity: item.disabled ? 0.5 : 1,
                  ...(index === 0 && {
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px'
                  }),
                  ...(index === items.length - 1 && {
                    borderBottomLeftRadius: '8px',
                    borderBottomRightRadius: '8px'
                  }),
                  ...(index === 0 &&
                    index === items.length - 1 && {
                      borderRadius: '8px'
                    }),
                  '&:hover': {
                    backgroundColor:
                      item.variant === 'danger' ? INTERAX_COLORS.redErrorBackground : INTERAX_COLORS.thunderGrey20
                  }
                }}
              >
                {item.icon && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: item.variant === 'danger' ? INTERAX_COLORS.redError : INTERAX_COLORS.white
                    }}
                  >
                    {item.icon}
                  </Box>
                )}
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.body2,
                    fontWeight: 500,
                    color: item.variant === 'danger' ? INTERAX_COLORS.redError : INTERAX_COLORS.white
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default DropdownMenu
